import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Globe, CreditCard, FileText, User, Star } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useItemStore } from '@/stores/item-store';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import { useDebounce } from '@/hooks/use-debounce';
import { ROUTES } from '@/constants/routes';
import type { DecryptedItem, ItemType } from '@/types/item';

const typeIcons: Record<ItemType, typeof Globe> = {
  LOGIN: Globe,
  CARD: CreditCard,
  NOTE: FileText,
  IDENTITY: User,
};

interface GlobalSearchProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function GlobalSearch({ open: controlledOpen, onOpenChange }: GlobalSearchProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);

  const typeLabels: Record<ItemType, string> = {
    LOGIN: t('item.type.login'),
    CARD: t('item.type.card'),
    NOTE: t('item.type.note'),
    IDENTITY: t('item.type.identity'),
  };
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DecryptedItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const { items, fetchItems, searchLocal } = useItemStore();

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  const debouncedQuery = useDebounce(query, 200);

  // Load items when dialog opens
  useEffect(() => {
    if (isOpen && items.length === 0) {
      setLoading(true);
      fetchItems().finally(() => setLoading(false));
    }
  }, [isOpen, items.length, fetchItems]);

  // Search when query changes
  useEffect(() => {
    if (debouncedQuery.trim()) {
      const searchResults = searchLocal(debouncedQuery);
      setResults(searchResults.slice(0, 10)); // Limit to 10 results
      setSelectedIndex(0);
    } else {
      // Show recent items when no query
      setResults(items.slice(0, 5));
      setSelectedIndex(0);
    }
  }, [debouncedQuery, items, searchLocal]);

  // Keyboard shortcut Ctrl+K
  useKeyboardShortcut(
    { key: 'k', ctrl: true },
    useCallback(() => setIsOpen(true), [setIsOpen])
  );

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % results.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        handleSelect(results[selectedIndex]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex]);

  const handleSelect = (item: DecryptedItem) => {
    setIsOpen(false);
    setQuery('');
    navigate(ROUTES.ITEM_DETAIL(item.id));
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setQuery('');
      setSelectedIndex(0);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg p-0">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="sr-only">{t('search.globalSearch')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('search.globalSearchDescription')}
          </DialogDescription>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('item.search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
        </DialogHeader>

        <div className="p-2 max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : results.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {query ? t('item.noResults') : t('search.noItems')}
            </p>
          ) : (
            <div className="space-y-1">
              {!query && (
                <p className="text-xs text-muted-foreground px-2 py-1">{t('search.recentItems')}</p>
              )}
              {results.map((item, index) => {
                const Icon = typeIcons[item.type];
                return (
                  <button
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className={`w-full flex items-center gap-3 p-2 rounded-md text-left transition-colors ${
                      index === selectedIndex
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent/50'
                    }`}
                  >
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{item.data.name}</span>
                        {item.isFavorite && (
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{typeLabels[item.type]}</span>
                        {item.data.username && (
                          <>
                            <span>·</span>
                            <span className="truncate">{item.data.username}</span>
                          </>
                        )}
                        {item.data.email && !item.data.username && (
                          <>
                            <span>·</span>
                            <span className="truncate">{item.data.email}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-2 border-t text-xs text-muted-foreground flex items-center gap-4">
          <span><kbd className="px-1.5 py-0.5 rounded bg-muted">↑↓</kbd> {t('search.toNavigate')}</span>
          <span><kbd className="px-1.5 py-0.5 rounded bg-muted">Enter</kbd> {t('search.toSelect')}</span>
          <span><kbd className="px-1.5 py-0.5 rounded bg-muted">Esc</kbd> {t('search.toClose')}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
